import type { User } from "./auth.js";
import type { Route } from "./router.js";

/**
 * Who to draw for, decided once at start -- with every effect handed in, so
 * the order of the decision is a fact a test can hold rather than a habit
 * `main.ts` has.
 */
export interface BootPorts {
  route: Route;
  /** Who this device was last signed in as. See `rememberedUser`. */
  remembered: () => User | undefined;
  pendingSignIn?: () => unknown;
  /** Whether a Sign out is still waiting for the server (g65). See `settlePendingLogout`. */
  pendingLogout: () => boolean;
  /** Sends that logout; a rejection means the server has not ended the session. */
  settleLogout: () => Promise<void>;
  /** Forgets the remembered user, who belongs to the session being ended. */
  forget: () => void;
  /** Asks the server. `undefined` is signed out; a rejection is unreachable. */
  currentUser: () => Promise<User | undefined>;
  /** Show the magic-link confirm screen for this token. */
  confirm: (token: string) => void;
  /** Open this user's mirror and mount the app. */
  begin: (user: User) => Promise<void>;
  signIn: () => void;
  unreachable: (error: unknown) => void;
  /**
   * Open the shared list a link names (g72). It needs no account: reading
   * asks nobody who is signed in, and the page learns who is looking from
   * the link's own answer.
   */
  shared?: () => void;
  /**
   * Whether the address carries a share link in its fragment. A list's own
   * path without one is a list in the visitor's Lists (g137), which the
   * account's app opens like any list of theirs.
   */
  linked?: () => boolean;
}

/**
 * The launch surface hands over the moment local initialisation is done, and
 * the network is never part of that (P04).
 *
 * A device that remembers its user opens the mirror at once and asks nobody.
 * The store's own open-time sync pass is the session check: a 401 there pauses
 * the engine, and the shell's notice says so and offers Sign in -- the same
 * path an expired session takes mid-use. Asking `/api/me` first, as this used
 * to, held the splash for as long as the request took, and `fetch` has no
 * timeout of its own; a bounded wait would still have been a wait on every
 * launch with a slow network, and the spec rules out the timer.
 *
 * With nothing remembered there is no mirror to open, so the server is the
 * only one who can say whether this is a sign-in or a device without a
 * network -- and those two are kept apart, because one is fixed by signing in
 * and the other is not.
 */
export async function boot(ports: BootPorts): Promise<void> {
  // Ahead of everything: arriving on a magic link while already signed in on
  // this device is ordinary, and it still has to redeem the token -- that is
  // how a second device gets its own session.
  if (ports.route.name === "confirm") {
    ports.confirm(ports.route.token);
    return;
  }

  // An unfinished Sign out comes before anything that could reach `begin`:
  // the cookie it left may still open the account, and the device promised to
  // hold nothing of it. The remembered user goes first, or the sign-in form
  // would offer "Back to saved lists" into a mirror that was wiped. Offline or
  // refused, this is still a sign-in and never the account -- unlike the
  // unreachable page below, which is for a device that is still signed in.
  if (ports.pendingLogout()) {
    ports.forget();
    try {
      await ports.settleLogout();
    } catch {
      ports.signIn();
      return;
    }
  }

  // A shared link opens its list whoever is signed in, or nobody (g72):
  // before a sign-in in progress, since a link is not a sign-in, and never
  // through `begin`, since the list is not the account's. After an unfinished
  // Sign out, though: the page would otherwise write as an account this
  // device said it had let go of. A grant's path with no link is an added
  // list (g137): the account's own app, through the account's start below.
  if (ports.route.name === "shared" && ports.shared && (ports.route.grant === undefined || ports.linked?.() === true)) {
    ports.shared();
    return;
  }

  if (ports.pendingSignIn?.()) {
    ports.signIn();
    return;
  }

  const remembered = ports.remembered();
  if (remembered) {
    await ports.begin(remembered);
    return;
  }

  let resolved: User | undefined;
  try {
    resolved = await ports.currentUser();
  } catch (error) {
    // Unreachable is not signed out: the sign-in form for someone merely on a
    // train is how a PWA loses the trust its offline story depends on.
    ports.unreachable(error);
    return;
  }

  if (!resolved) {
    ports.signIn();
    return;
  }
  await ports.begin(resolved);
}
